import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { ShoppingBag, Filter, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { resolveImageUrl } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const collection = searchParams.get('collection');
  const brand = searchParams.get('brand');
  const search = searchParams.get('search');
  const subcategory = searchParams.get('subcategory');
  // Also check for category in URL params (for routes like /products?category=Wall Clocks)
  const categoryFromParams = searchParams.get('category');
  // Use category from URL path OR from query params
  const effectiveCategory = category || categoryFromParams;

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart } = useCart();
  
  // Dynamic brands from API
  const [brands, setBrands] = useState(['All']);

  // Sync URL params with state
  useEffect(() => {
    setSelectedBrand(brand || '');
    setSelectedCollection(collection || '');
  }, [brand, collection]);

  // Fetch brands dynamically based on category
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        // Determine if we're on the "Brands" page (no category) or a specific category page
        const isWatchSubcategory = ['Men', 'Women', 'Kids', 'Unisex'].includes(effectiveCategory);
        
        if (isWatchSubcategory) {
          // For Watches subcategories (Men, Women, Kids, Unisex), fetch brands from products
          // that actually have products in that subcategory
          const response = await axios.get(`${API}/products/brands-by-category?category=${encodeURIComponent(effectiveCategory)}`);
          const brandNames = response.data || [];
          setBrands(['All', ...brandNames]);
        } else if (effectiveCategory) {
          // For other categories (Wall Clocks, Accessories, Smart Watches), use existing logic
          let url = `${API}/brands?category=${encodeURIComponent(effectiveCategory)}`;
          if (subcategory) {
            url += `&subcategory=${encodeURIComponent(subcategory)}`;
          }
          const response = await axios.get(url);
          const brandNames = response.data.map(b => b.name);
          setBrands(['All', ...brandNames]);
        } else {
          // No category selected (Brands page from navigation) - fetch all brands but deduplicate by name
          const response = await axios.get(`${API}/brands`);
          const uniqueBrandNames = [...new Set(response.data.map(b => b.name))].sort();
          setBrands(['All', ...uniqueBrandNames]);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        setBrands(['All']);
      }
    };
    
    fetchBrands();
  }, [effectiveCategory, subcategory]);

  const collections = ['All', 'Best Selling', 'Luxury', 'Best Budget', 'Astrological'];
  const colors = ['All', 'Black', 'Silver', 'Gold', 'Rose Gold', 'Blue', 'Green', 'Brown', 'White', 'Multi-Color'];
  const types = ['All', 'Analog', 'Digital', 'Chronograph', 'Smart Watch', 'Hybrid'];

  useEffect(() => {
    fetchProducts();
  }, [effectiveCategory, collection, brand, search, subcategory, selectedBrand, selectedCollection]);

  useEffect(() => {
    filterProducts();
  }, [products, selectedColor, selectedType, priceRange]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/products`;
      const params = new URLSearchParams();
      
      // Use effectiveCategory (from path OR query params)
      if (effectiveCategory) params.append('category', effectiveCategory);
      
      // Use URL brand param OR selectedBrand state (whichever is set)
      const brandToUse = brand || selectedBrand;
      if (brandToUse && brandToUse !== 'All') params.append('brand', brandToUse);
      
      // Use URL collection param OR selectedCollection state (whichever is set)
      const collectionToUse = collection || selectedCollection;
      if (collectionToUse && collectionToUse !== 'All') params.append('collection', collectionToUse);
      
      if (search) params.append('search', search);
      if (subcategory) params.append('subcategory', subcategory);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (selectedColor && selectedColor !== 'All') {
      filtered = filtered.filter(p => p.color === selectedColor);
    }

    if (selectedType && selectedType !== 'All') {
      filtered = filtered.filter(p => p.type === selectedType);
    }

    filtered = filtered.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);

    setFilteredProducts(filtered);
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Toaster />

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className="lg:hidden mb-3 sm:mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full bg-black text-white py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Filters</span>
              </button>
            </div>

            <div className={`bg-white rounded-lg p-4 sm:p-6 sticky top-24 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="flex justify-between items-center mb-4 lg:hidden">
                <h3 className="text-lg font-bold">Filters</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Brand Filter */}
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Brand</h3>
                <div className="space-y-1 sm:space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
                  {brands.map((b) => (
                    <label key={b} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1.5 sm:p-2 rounded text-sm sm:text-base">
                      <input
                        type="radio"
                        name="brand"
                        checked={selectedBrand === b || (b === 'All' && !selectedBrand)}
                        onChange={() => {
                          const newBrand = b === 'All' ? '' : b;
                          setSelectedBrand(newBrand);
                          // Update URL params
                          const params = new URLSearchParams(searchParams);
                          if (newBrand) {
                            params.set('brand', newBrand);
                          } else {
                            params.delete('brand');
                          }
                          setSearchParams(params);
                        }}
                        className="text-yellow-500 focus:ring-yellow-500"
                      />
                      <span>{b}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Collection Filter */}
              <div className="mb-4 sm:mb-6 border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Collection</h3>
                <div className="space-y-1 sm:space-y-2">
                  {collections.map((c) => (
                    <label key={c} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1.5 sm:p-2 rounded text-sm sm:text-base">
                      <input
                        type="radio"
                        name="collection"
                        checked={selectedCollection === c || (c === 'All' && !selectedCollection)}
                        onChange={() => {
                          const newCollection = c === 'All' ? '' : c;
                          setSelectedCollection(newCollection);
                          // Update URL params
                          const params = new URLSearchParams(searchParams);
                          if (newCollection) {
                            params.set('collection', newCollection);
                          } else {
                            params.delete('collection');
                          }
                          setSearchParams(params);
                        }}
                        className="text-yellow-500 focus:ring-yellow-500"
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Color Filter */}
              <div className="mb-4 sm:mb-6 border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Color</h3>
                <div className="space-y-1 sm:space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
                  {colors.map((c) => (
                    <label key={c} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1.5 sm:p-2 rounded text-sm sm:text-base">
                      <input
                        type="radio"
                        name="color"
                        checked={selectedColor === c || (c === 'All' && !selectedColor)}
                        onChange={() => setSelectedColor(c === 'All' ? '' : c)}
                        className="text-yellow-500 focus:ring-yellow-500"
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Type Filter */}
              <div className="mb-4 sm:mb-6 border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Type</h3>
                <div className="space-y-1 sm:space-y-2">
                  {types.map((t) => (
                    <label key={t} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1.5 sm:p-2 rounded text-sm sm:text-base">
                      <input
                        type="radio"
                        name="type"
                        checked={selectedType === t || (t === 'All' && !selectedType)}
                        onChange={() => setSelectedType(t === 'All' ? '' : t)}
                        className="text-yellow-500 focus:ring-yellow-500"
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3">Price Range</h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span>₹{priceRange[0]}</span>
                    <span>₹{priceRange[1]}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="50000"
                    step="500"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedBrand('');
                  setSelectedCollection('');
                  setSelectedColor('');
                  setSelectedType('');
                  setPriceRange([0, 50000]);
                  // Clear URL params
                  const params = new URLSearchParams(searchParams);
                  params.delete('brand');
                  params.delete('collection');
                  setSearchParams(params);
                }}
                className="w-full mt-4 sm:mt-6 bg-gray-200 hover:bg-gray-300 text-black py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Reset Filters
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:w-3/4">
            {loading ? (
              <div className="flex justify-center items-center h-48 sm:h-64">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-yellow-500"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <p className="text-xl sm:text-2xl text-gray-500">No products found</p>
                <Link to="/products" className="text-yellow-600 hover:text-yellow-700 mt-4 inline-block text-sm sm:text-base">
                  View all products
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                {filteredProducts.map((product) => {
                  // For products with color variants, use first variant's image and price
                  const hasVariants = product.color_variants && product.color_variants.length > 0;
                  const rawDisplayImage = hasVariants 
                    ? (product.color_variants[0]?.media?.[0]?.url || product.image)
                    : product.image;
                  const displayImage = resolveImageUrl(rawDisplayImage);
                  const displayPrice = hasVariants ? product.color_variants[0].price : product.price;
                  const displayOriginalPrice = hasVariants 
                    ? product.color_variants[0].original_price 
                    : product.original_price;
                  
                  return (
                  <Link
                    key={product.id}
                    to={`/product/${product.slug || product.id}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <div className="relative overflow-hidden aspect-[4/3] sm:aspect-square">
                      <img
                        src={displayImage}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {displayOriginalPrice && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold">
                          {Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)}% OFF
                        </div>
                      )}
                      {/* Color variants indicator */}
                      {hasVariants && product.color_variants.length > 1 && (
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs">
                          {product.color_variants.length} Colors
                        </div>
                      )}
                    </div>
                    <div className="p-2.5 sm:p-4">
                      <p className="text-[10px] sm:text-sm text-yellow-600 font-semibold mb-0.5">{product.brand}</p>
                      <h3 className="text-xs sm:text-base font-semibold text-gray-800 mb-1 line-clamp-2 h-8 sm:h-12">{product.name}</h3>
                      {product.review_count > 0 && (
                        <div className="flex items-center gap-1 mb-1" data-testid={`product-card-rating-${product.id}`}>
                          <span className="text-yellow-500 text-xs sm:text-sm">★</span>
                          <span className="text-[10px] sm:text-xs font-semibold text-gray-700">
                            {(product.average_rating || 0).toFixed(1)}
                          </span>
                          <span className="text-[10px] sm:text-xs text-gray-500">
                            ({product.review_count})
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm sm:text-lg md:text-xl font-bold text-gray-900">₹{displayPrice.toLocaleString()}</p>
                          {displayOriginalPrice && (
                            <p className="text-[10px] sm:text-sm text-gray-500 line-through">₹{displayOriginalPrice.toLocaleString()}</p>
                          )}
                        </div>
                        <button
                          onClick={(e) => handleAddToCart(product, e)}
                          className="bg-yellow-500 hover:bg-yellow-600 text-black px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors duration-300 flex items-center space-x-1"
                        >
                          <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="text-[10px] sm:text-sm font-semibold hidden sm:inline">Add</span>
                        </button>
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Products;
